import React from 'react';
import { Link } from 'react-router-dom';
import { useSEO } from '../hooks/useSEO';
import { GUIAS_PAGE, SEO_PAGES, SUPPORT_PAGES, buildPageJsonLd } from '../lib/site';
import { GUIDE_GROUPS, getGuidePage } from '../lib/guideArchitecture';

function SectionHeading({ title, description, id }) {
  return (
    <div className="mb-2 border-b border-[var(--hairline)] pb-3">
      <h2 id={id} className="mb-1">{title}</h2>
      <p className="text-[var(--text-muted)]">{description}</p>
    </div>
  );
}

function GuideRow({ to, title, description }) {
  return (
    <li className="border-b border-[var(--border)] pb-4">
      <Link to={to} className="group no-underline">
        <span className="block text-[length:var(--fs-17)] font-semibold text-[var(--ink)] group-hover:underline underline-offset-4">{title}</span>
        <span className="mt-1 block text-[length:var(--fs-15)] leading-snug text-[var(--ink-2)]">{description}</span>
      </Link>
    </li>
  );
}

export default function Guias() {
  useSEO({ title: GUIAS_PAGE.title, description: GUIAS_PAGE.description, path: GUIAS_PAGE.path });

  return (
    <main className="mx-auto max-w-[980px] px-4 py-10 md:py-16">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(buildPageJsonLd(GUIAS_PAGE)) }}
      />

      <nav aria-label="Trilha de navegação" className="mb-8 text-sm text-[var(--text-muted)]">
        <ol className="flex flex-wrap gap-2">
          <li><Link to="/" className="hover:underline">Mini Mental</Link></li>
          <li aria-hidden="true">›</li>
          <li aria-current="page" className="font-semibold text-[var(--primary)]">Guias do MEEM</li>
        </ol>
      </nav>

      <header className="mb-14 max-w-[760px]">
        <h1 className="mb-4">Guias do Mini Mental (MEEM)</h1>
        <p className="text-xl leading-relaxed text-[var(--primary)]">
          Encontre o conteúdo pela decisão que você precisa tomar: entender o exame, aplicar corretamente, encontrar a referência brasileira, interpretar um resultado, acompanhar mudanças ou escolher um instrumento complementar.
        </p>
      </header>

      <div className="space-y-10">
        <section id="essenciais" aria-labelledby="essenciais-titulo" className="scroll-mt-28">
          <SectionHeading
            id="essenciais-titulo"
            title="Recursos essenciais do Mini Mental"
            description="Páginas de entrada para quem procura o exame, a referência em PDF ou a tarefa visuoconstrutiva."
          />
          <ul className="mt-4 grid gap-x-10 md:grid-cols-2">
            {SEO_PAGES.map((page) => (
              <GuideRow key={page.path} to={page.path} title={page.breadcrumb} description={page.description} />
            ))}
          </ul>
        </section>

        {GUIDE_GROUPS.map((group) => (
          <section key={group.id} id={group.id} aria-labelledby={`${group.id}-titulo`} className="scroll-mt-28">
            <SectionHeading
              id={`${group.id}-titulo`}
              title={group.title}
              description={group.description}
            />
            <ul className="mt-4 grid gap-x-10 md:grid-cols-2">
              {group.paths.map((path) => {
                const page = getGuidePage(path);
                return <GuideRow key={path} to={path} title={page.breadcrumb} description={page.description} />;
              })}
            </ul>
          </section>
        ))}

        <section id="base-metodologia" aria-labelledby="base-metodologia-titulo" className="scroll-mt-28">
          <SectionHeading
            id="base-metodologia-titulo"
            title="Base, metodologia e transparência"
            description="Veja de onde vêm os números, como cada referência brasileira é usada e onde terminam as conclusões da ferramenta."
          />
          <ul className="mt-4 grid gap-x-10 md:grid-cols-2">
            {SUPPORT_PAGES.map((page) => (
              <GuideRow key={page.path} to={page.path} title={page.breadcrumb} description={page.description} />
            ))}
          </ul>
        </section>
      </div>

      <section className="mt-12 flex flex-col gap-3 border-t border-[var(--border-strong)] pt-6 md:flex-row md:items-baseline md:justify-between">
        <div>
          <h2 className="mb-1">Já aplicou o MEEM?</h2>
          <p className="text-sm text-[var(--text-muted)]">Use a calculadora para somar os pontos por domínio e então siga para a interpretação.</p>
        </div>
        <Link to="/#calculadora" className="inline-flex min-h-11 items-center whitespace-nowrap text-[var(--primary)] underline">Ir para a calculadora →</Link>
      </section>
    </main>
  );
}
